import React, { ReactNode } from "react";
import { useMediaQuery } from "react-responsive";
import Button from "../../components/common/Button";
import { getSocialMedias } from "../../backend/socialMedia/socialMedia";
import { SocialMedia } from "../../types/socialMedia/socialMedia";
import SocialMediaRow from "../../components/common/SocialMediaRow";
import { usePopup } from "../../components/PopupContext";
import { getMainInfoByName } from "../../backend/info/mainInfo";
import formatPhoneNumber from "../../utils/formatNumber";
import { getHowToHelp } from "../../backend/howToHelp/howToHelp";

const HowToHelpCard = ({
  firstWord,
  heading,
  description,
}: {
  firstWord: string;
  heading: string;
  description: string;
}) => {
  const isMedium = useMediaQuery({ query: `(max-width: 700px)` });
  const isSmall = useMediaQuery({ query: `(max-width: 450px)` });
  return (
    <div
      style={{
        ...styles.howToHelpCardContainer,
        paddingRight: isSmall ? "0rem" : isMedium ? "1rem" : "5rem",
        gap: isSmall ? "1rem" : isMedium ? "1.5rem" : "2rem",
      }}
    >
      <div
        style={{
          ...styles.bulletPoint,
          marginTop: isSmall ? "0.5rem" : isMedium ? "0.75rem" : "0.875rem",
        }}
      />
      <div style={styles.infoColumn}>
        <h3
          style={{
            ...styles.howToHelpName,
            fontSize: isSmall ? "1.5rem" : isMedium ? "2rem" : "2.5rem",
          }}
        >
          <span>{firstWord} </span>
          {heading}
        </h3>
        <p
          style={{
            ...styles.howToHelpDescription,
            fontSize: isSmall ? "1rem" : isMedium ? "1.2rem" : "1.5rem",
            whiteSpace: "pre-line",
          }}
        >
          {description}
        </p>
      </div>
    </div>
  );
};

const HowToHelpMainCard = ({
  firstWord,
  heading,
  description,
}: {
  firstWord: string;
  heading: string;
  description: string;
}) => {
  const isSmall = useMediaQuery({ query: `(max-width: 450px)` });
  const isSingle = useMediaQuery({ query: `(max-width: 900px)` });
  const isMedium = useMediaQuery({ query: `(max-width: 700px)` });
  return (
    <div style={{ ...styles.howToHelpColumn, paddingBottom: "2rem" }}>
      <div
        style={{ ...styles.howToHelpMainRow, gridTemplateColumns: isSingle ? "1fr" : "1fr 1fr" }}
      >
        <HowToHelpCard firstWord={firstWord} heading={heading} description={description} />
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            marginTop: isSmall ? "2rem" : isSingle ? "2rem" : "0rem",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              width: "100%",
              alignItems: "end",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                justifyContent: "center  ",
                gap: isSmall ? "1rem" : "2.25rem",
              }}
            >
              <Button
                text={"Замовити дзвінок"}
                external_link={getMainInfoByName("anketaLink")}
                textColor="var(--white)"
                hoverTextColor="var(--black)"
                hoverBorder="3px solid var(--black)"
                backgroundColor="var(--black)"
                hoverBackgroundColor="var(--white)"
                fontSize={isMedium ? "8vw" : "min(2.5rem, 6vw)"}
                lineHeight="min(6rem,15vw)"
                padding="0.5rem 1rem"
                fontWeight="bold"
                width="100%"
                containerProps={{
                  width: isMedium ? "var(--main-container-width)" : "",
                }}
              />
              <Button
                text={getMainInfoByName("recruitmentPhone")}
                external_link={formatPhoneNumber(getMainInfoByName("recruitmentPhone"))}
                border="3px solid var(--white)"
                hoverBorder="3px solid var(--black)"
                textColor="var(--white)"
                hoverTextColor="var(--black)"
                fontWeight="bold"
                fontSize={isMedium ? "8vw" : "min(2.5rem, 6vw)"}
                lineHeight="min(6rem,15vw)"
                padding="0.5rem 1rem"
                iconPath="/assets/icons/tel.svg"
                iconProps={{
                  width: "min(3rem,10vw)",
                  height: "min(3rem,10vw)",
                  marginRight: "1rem",
                }}
                iconColor="var(--white)"
                hoverIconColor="var(--black)"
                width="100%"
                containerProps={{
                  width: isMedium ? "var(--main-container-width)" : "",
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          ...styles.howToHelpBar,
          marginLeft: isSingle ? "0rem" : "calc(2rem + 15px)",
        }}
      />
    </div>
  );
};

const HowToHelpSideCard = ({
  firstWord,
  heading,
  description,
  children = undefined,
  showDivider = true,
}: {
  firstWord: string;
  heading: string;
  description: string;
  children: ReactNode;
  showDivider: boolean;
}) => {
  const isSingle = useMediaQuery({ query: `(max-width: 850px)` });
  return (
    <div style={styles.howToHelpColumn}>
      <HowToHelpCard firstWord={firstWord} heading={heading} description={description} />
      <div style={{ display: "flex", flexDirection: "column", marginTop: "2rem" }}>
        {children}
        {showDivider && (
          <div
            style={{ ...styles.howToHelpBar, marginLeft: isSingle ? "0rem" : "calc(2rem + 15px)" }}
          />
        )}
      </div>
    </div>
  );
};

const HowToHelp = () => {
  const { show } = usePopup();

  const howToHelpList = getHowToHelp();
  const isSingle = useMediaQuery({ query: `(max-width: 950px)` });
  const isMedium = useMediaQuery({ query: `(max-width: 700px)` });
  const isSmall = useMediaQuery({ query: `(max-width: 450px)` });
  return (
    <div style={styles.howToHelpWrapper}>
      <div style={{ ...styles.howToHelpContainer, padding: isSingle ? "2rem 0rem" : "2rem 0rem" }}>
        <h2
          style={{
            ...styles.header,
            marginBottom: isSmall ? "1.7rem" : isSingle ? "2.7rem" : "3.7rem",
            fontSize: isSmall ? "min(2.5rem,10vw)" : isMedium ? "3.5rem" : "5rem",
          }}
        >
          Як наблизити перемогу?
        </h2>
        <HowToHelpMainCard
          firstWord={howToHelpList[0].firstWord}
          heading={howToHelpList[0].heading}
          description={howToHelpList[0].description}
        />
        <div
          style={{
            ...styles.howToHelpGrid,
            gridTemplateColumns: isSingle ? "1fr" : "1fr 1fr",
          }}
        >
          {howToHelpList.map((item, index) => {
            if (index > 0)
              return (
                <HowToHelpSideCard
                  firstWord={item.firstWord}
                  heading={item.heading}
                  description={item.description}
                  showDivider={
                    isSingle ? index < howToHelpList.length - 1 : index < howToHelpList.length - 2
                  }
                  children={
                    index == 1 ? (
                      <SocialMediaRow
                        rowStyle={{
                          // marginBottom: isSingle ? "0rem" : "3rem",
                          // marginTop: isSingle ? "1rem" : "3rem",
                          marginLeft: isSingle ? "" : "calc(15px + 2rem)",
                          width: isSingle ? "var(--main-container-width)" : "",
                          justifyContent: isSmall ? "space-between" : isSingle ? "center" : "",
                        }}
                        size={isSmall ? "16vw" : "min(4rem, 12vw)"}
                      />
                    ) : index == 2 ? (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: isSingle ? "column" : "row",
                          gap: "1rem",
                          marginLeft: isSingle ? "" : "calc(2rem + 15px)",
                        }}
                      >
                        <Button
                          text={"Актуальний збір"}
                          external_link={getMainInfoByName("supportLink")}
                          border="3px solid var(--black)"
                          hoverBorder="3px solid var(--black)"
                          textColor="var(--white)"
                          hoverTextColor="var(--black)"
                          backgroundColor="var(--black)"
                          hoverBackgroundColor="var(--white)"
                          fontWeight="normal"
                          fontSize={isMedium ? "6vw" : "min(1.5rem, 4vw)"}
                          lineHeight="min(6rem,15vw)"
                          padding="0rem 0.5rem"
                          containerProps={{
                            alignSelf: isSingle ? "center" : "flex-start",
                            width: isSingle
                              ? "100%"
                              : isMedium
                                ? "calc(var(--main-container-width)/2 - 0.5rem)"
                                : "50%",
                          }}
                          height="min(16vw, 4rem)"
                        />
                        <Button
                          text={"Підписка"}
                          external_link={getMainInfoByName("subscriptionLink")}
                          border="3px solid var(--black)"
                          hoverBorder="3px solid var(--black)"
                          textColor="var(--black)"
                          hoverTextColor="var(--orange)"
                          backgroundColor="transparent"
                          hoverBackgroundColor="var(--black)"
                          fontWeight="normal"
                          fontSize={isMedium ? "6vw" : "min(1.5rem, 4vw)"}
                          lineHeight="min(6rem,15vw)"
                          padding="0rem 0.5rem"
                          containerProps={{
                            alignSelf: isSingle ? "center" : "flex-start",
                            width: isSingle
                              ? "100%"
                              : isMedium
                                ? "calc(var(--main-container-width)/2 - 0.5rem)"
                                : "50%",
                          }}
                          height="min(16vw, 4rem)"
                        />
                      </div>
                    ) : index == 3 ? (
                      <Button
                        text={"До магазину"}
                        internal_link={"/shop"}
                        border="3px solid var(--black)"
                        hoverBorder="3px solid var(--black)"
                        textColor="var(--white)"
                        hoverTextColor="var(--black)"
                        backgroundColor="var(--black)"
                        hoverBackgroundColor="var(--white)"
                        fontWeight="normal"
                        fontSize={isMedium ? "6vw" : "min(1.5rem, 4vw)"}
                        lineHeight="min(6rem,15vw)"
                        padding="0rem 0.5rem"
                        containerProps={{
                          alignSelf: isSingle ? "center" : "flex-start",
                          width: isMedium ? "var(--main-container-width)" : "50%",
                        }}
                        marginLeft={isSingle ? "" : "calc(2rem + 15px)"}
                        height="min(16vw, 4rem)"
                      />
                    ) : (
                      <Button
                        text={"Відгукнутися"}
                        onClick={() => {
                          show(true);
                        }}
                        border="3px solid var(--black)"
                        hoverBorder="3px solid var(--black)"
                        textColor="var(--white)"
                        hoverTextColor="var(--black)"
                        backgroundColor="var(--black)"
                        hoverBackgroundColor="var(--white)"
                        fontWeight="normal"
                        fontSize={isMedium ? "6vw" : "min(1.5rem, 4vw)"}
                        lineHeight="min(6rem,15vw)"
                        padding="0rem 0.5rem"
                        containerProps={{
                          alignSelf: isSingle ? "center" : "flex-start",
                          width: isMedium ? "var(--main-container-width)" : "50%",
                        }}
                        marginLeft={isSingle ? "" : "calc(2rem + 15px)"}
                        height="min(16vw, 4rem)"
                      />
                    )
                  }
                />
              );
          })}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  howToHelpWrapper: {
    display: "flex",
    width: "100%",
    backgroundColor: "#a7a994",
    overflow: "hidden",
  },
  howToHelpContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    width: "var(--main-container-width)",
    margin: "0 auto",
    overflow: "hidden",
  },
  header: {
    fontSize: "5rem",
    fontWeight: "bold",
    color: "var(--black)",
  },
  howToHelpGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "2rem 0rem",
  },
  howToHelpCardContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "2rem",
    paddingRight: "5rem",
  },
  bulletPoint: {
    width: "15px",
    height: "15px",
    marginTop: "0.85rem",
    backgroundColor: "var(--black)",
    flexShrink: 0,
  },
  infoColumn: {
    display: "flex",
    flexDirection: "column",
  },
  howToHelpName: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    color: "var(--black)",
    marginBottom: "1rem",
  },
  howToHelpDescription: {
    fontSize: "1.25rem",
    color: "var(--grey)",
  },
  howToHelpColumn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  howToHelpBar: {
    display: "block",
    marginTop: "2rem",
    marginLeft: "calc(2rem + 15px)",
    height: "1.5px",
    backgroundColor: "var(--black)",
  },
  howToHelpMainRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
  },
};

export default HowToHelp;
